import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import {
  SlidersHorizontal,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Lock,
} from "lucide-react";
import { FormField, Input, Select, Textarea } from "../../components/UI/FormField";
import { SectionCard } from "../../components/UI/SectionCard";
import { withVersion } from "../../utils/concurrency";
import Button from "../../components/UI/Button";
import { ConfirmDialog } from "../../components/UI/ConfirmDialog";
import { companyAPI } from "../../services/api";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { getCountries } from "../../store/slices/companySlice";
import type { Company } from "../../types";
import type {
  InvoiceNumbering,
  InvoicingReadiness,
} from "../../types/invoice";

// The highest invoice number the server accepts (INVOICE_RANGE_LIMIT).
const RANGE_LIMIT = 999_999_999;

interface FormState {
  name: string;
  registrationOrLicenseNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalcode: string;
    country: string;
  };
  invoicing: {
    legalName: string;
    vatNumber: string;
    registryNumber: string;
    iban: string;
    bankName: string;
    invoiceFooter: string;
    defaultPaymentTerms: number;
  };
}

const emptyForm: FormState = {
  name: "",
  registrationOrLicenseNumber: "",
  address: { street: "", city: "", state: "", postalcode: "", country: "" },
  invoicing: {
    legalName: "",
    vatNumber: "",
    registryNumber: "",
    iban: "",
    bankName: "",
    invoiceFooter: "",
    defaultPaymentTerms: 14,
  },
};

const OrganisationSettings: React.FC = () => {
  const { t } = useTranslation("invoicing");
  const dispatch = useAppDispatch();
  const countries = useAppSelector((s) => s.company.countries);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [readiness, setReadiness] = useState<InvoicingReadiness>({
    ready: false,
    missing: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // OCC — preserve the company's version at load time to send with the update.
  const [version, setVersion] = useState<string | undefined>();
  const fileRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // ── Invoice number range ─────────────────────────────────────────────────
  // Saved on its own, not with the form above: the server checks it against
  // the numbers locks are taking right now, not against a loaded version.
  const [numbering, setNumbering] = useState<InvoiceNumbering | null>(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [rangeSaving, setRangeSaving] = useState(false);
  const [confirmRange, setConfirmRange] = useState(false);
  const numberingRef = useRef<HTMLDivElement>(null);

  const hydrateNumbering = (n: InvoiceNumbering | null) => {
    setNumbering(n);
    setRange({ start: n ? String(n.start) : "", end: n ? String(n.end) : "" });
  };

  const countryOptions = useMemo(
    () => countries.map((c: any) => ({ value: c._id, label: c.name })),
    [countries],
  );

  const hydrate = (company: Company, rdy?: InvoicingReadiness) => {
    const inv = company.invoicing || {};
    setForm({
      name: company.name || "",
      registrationOrLicenseNumber: company.registrationOrLicenseNumber || "",
      address: {
        street: company.address?.street || "",
        city: company.address?.city || "",
        state: company.address?.state || "",
        postalcode:
          (company.address as any)?.postalcode ||
          (company.address as any)?.zipcode ||
          "",
        country:
          typeof company.address?.country === "object"
            ? (company.address?.country as any)?._id
            : (company.address?.country as any) || "",
      },
      invoicing: {
        legalName: inv.legalName || "",
        vatNumber: inv.vatNumber || "",
        registryNumber: inv.registryNumber || "",
        iban: inv.iban || "",
        bankName: inv.bankName || "",
        invoiceFooter: inv.invoiceFooter || "",
        defaultPaymentTerms: inv.defaultPaymentTerms || 14,
      },
    });
    setLogoUrl(inv.logoUrl || "");
    setVersion((company as any).updatedAt);
    if (rdy) setReadiness(rdy);
  };

  useEffect(() => {
    dispatch(getCountries());
    (async () => {
      try {
        const res = await companyAPI.getCompany();
        hydrate(res.data.data.company, res.data.data.invoicingReadiness);
        hydrateNumbering(res.data.data.invoiceNumbering ?? null);
      } catch {
        toast.error(t("orgSettings.loadFailed"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arriving from a lock refused for a used-up range lands on the range.
  useEffect(() => {
    if (!loading && location.hash === "#invoice-numbering") {
      numberingRef.current?.scrollIntoView({ block: "start" });
    }
  }, [loading, location.hash]);

  const set = (path: string, value: any) => {
    setForm((prev) => {
      const next = structuredClone(prev) as any;
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await companyAPI.updateInvoicingSettings(
        withVersion(form, { updatedAt: version }),
      );
      hydrate(res.data.data.company, res.data.data.invoicingReadiness);
      toast.success(t("orgSettings.saved"));

      rootRef.current?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("orgSettings.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await companyAPI.uploadInvoiceLogo(file);
      setLogoUrl(res.data.data.logoUrl);
      setReadiness(res.data.data.invoicingReadiness);
      // The upload wrote to the company, so the version this form is holding is
      // now stale — adopt the one the write produced or the next Save conflicts
      // with this very upload.
      if (res.data.data.updatedAt) setVersion(res.data.data.updatedAt);
      toast.success(t("orgSettings.logoUploaded"));
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("orgSettings.logoFailed"));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const rangeStart = Number(range.start);
  const rangeEnd = Number(range.end);
  const rangeDirty = numbering
    ? range.start !== String(numbering.start) ||
      range.end !== String(numbering.end)
    : !!(range.start || range.end);
  const rangeErrors = (() => {
    const out: { start?: string; end?: string } = {};
    if (!rangeDirty) return out;
    const whole = (n: number) =>
      Number.isInteger(n) && n >= 1 && n <= RANGE_LIMIT;
    const notWhole = t("orgSettings.rangeWholeNumber", { max: RANGE_LIMIT });
    if (!whole(rangeStart)) out.start = notWhole;
    if (!whole(rangeEnd)) out.end = notWhole;
    else if (whole(rangeStart) && rangeEnd < rangeStart)
      out.end = t("orgSettings.rangeEndBelowStart");
    else if (numbering?.startLocked && rangeEnd < numbering.next)
      out.end = t("orgSettings.rangeEndBelowNext", { next: numbering.next });
    return out;
  })();
  const rangeValid = !rangeErrors.start && !rangeErrors.end;
  // Warned before it runs out: at 10% left, but never earlier than 100 left
  // nor later than 10.
  const rangeSize = numbering ? numbering.end - numbering.start + 1 : 0;
  const lowOnNumbers =
    !!numbering &&
    !numbering.exhausted &&
    numbering.remaining <=
      Math.min(100, Math.max(10, Math.ceil(rangeSize / 10)));

  const saveRange = async () => {
    setConfirmRange(false);
    setRangeSaving(true);
    try {
      const res = await companyAPI.updateInvoiceNumberRange({
        start: rangeStart,
        end: rangeEnd,
      });
      hydrateNumbering(res.data.data.invoiceNumbering);
      // The range is required to invoice, so setting it can lift the banner.
      if (res.data.data.invoicingReadiness) {
        setReadiness(res.data.data.invoicingReadiness);
      }
      toast.success(t("orgSettings.rangeSaved"));
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("orgSettings.rangeSaveFailed"));
      // Refused because invoices were issued meanwhile: show where the range
      // stands now, and keep what the user typed so they can adjust it.
      if (e.response?.status === 409) {
        try {
          const res = await companyAPI.getCompany();
          setNumbering(res.data.data.invoiceNumbering ?? null);
        } catch {
          /* the toast already says what went wrong */
        }
      }
    } finally {
      setRangeSaving(false);
    }
  };

  // Choosing the first number is the one part that can't be undone once an
  // invoice is issued from it, so that is asked about; raising the limit isn't.
  const handleSaveRange = () => {
    if (!rangeValid || !rangeDirty) return;
    const setsStart = !numbering || range.start !== String(numbering.start);
    if (setsStart) setConfirmRange(true);
    else saveRange();
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-[#8A9590]">
        {t("orgSettings.loading")}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <SlidersHorizontal className="h-6 w-6 text-[#C97A3C]" />
        <div>
          <h1 className="text-xl font-medium text-[#0B0F0E]">
            {t("orgSettings.title")}
          </h1>
          <p className="text-[13px] text-[#8A9590]">
            {t("orgSettings.subtitle")}
          </p>
        </div>
      </div>

      {readiness.ready ? (
        <div className="flex items-center gap-2 rounded-lg bg-[#EAF3DE] border border-[#C0DD97] text-[#27500A] px-4 py-2.5 text-[13px]">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {t("orgSettings.ready")}
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-lg bg-[#FAEEDA] border border-[#FAC775] text-[#633806] px-4 py-2.5 text-[13px]">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            {t("orgSettings.blocked")}{" "}
            <strong>
              {readiness.missing
                .map((m) => t(`readinessLabels.${m}`, { defaultValue: m }))
                .join(", ")}
            </strong>
          </div>
        </div>
      )}

      <SectionCard title={t("orgSettings.legalIdentity")}>
        <div className="space-y-3">
          <FormField layout="inline" size="sm" label={t("orgSettings.companyName")} required>
            <Input
              size="sm"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </FormField>
          <FormField
            layout="inline"
            size="sm"
            label={t("orgSettings.legalName")}
            hint={t("orgSettings.legalNameHint")}
          >
            <Input
              size="sm"
              value={form.invoicing.legalName}
              onChange={(e) => set("invoicing.legalName", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.registrationNo")} required>
            <Input
              size="sm"
              value={form.registrationOrLicenseNumber}
              onChange={(e) =>
                set("registrationOrLicenseNumber", e.target.value)
              }
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.vatNumber")} required>
            <Input
              size="sm"
              value={form.invoicing.vatNumber}
              onChange={(e) => set("invoicing.vatNumber", e.target.value)}
            />
          </FormField>
        </div>
      </SectionCard>

      <SectionCard title={t("orgSettings.registeredAddress")}>
        <div className="space-y-3">
          <FormField layout="inline" size="sm" label={t("orgSettings.street")} required>
            <Input
              size="sm"
              value={form.address.street}
              onChange={(e) => set("address.street", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.city")} required>
            <Input
              size="sm"
              value={form.address.city}
              onChange={(e) => set("address.city", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.state")}>
            <Input
              size="sm"
              value={form.address.state}
              onChange={(e) => set("address.state", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.postalCode")}>
            <Input
              size="sm"
              value={form.address.postalcode}
              onChange={(e) => set("address.postalcode", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.country")} required>
            <Select
              size="sm"
              placeholder={t("orgSettings.selectCountry")}
              options={countryOptions}
              value={form.address.country}
              onChange={(e) => set("address.country", e.target.value)}
            />
          </FormField>
        </div>
      </SectionCard>

      <SectionCard title={t("orgSettings.bankingBranding")}>
        <div className="space-y-3">
          <FormField layout="inline" size="sm" label={t("orgSettings.iban")} required>
            <Input
              size="sm"
              value={form.invoicing.iban}
              onChange={(e) => set("invoicing.iban", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.bankName")}>
            <Input
              size="sm"
              value={form.invoicing.bankName}
              onChange={(e) => set("invoicing.bankName", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.defaultPaymentTerms")}>
            <Select
              size="sm"
              value={String(form.invoicing.defaultPaymentTerms)}
              onChange={(e) =>
                set("invoicing.defaultPaymentTerms", Number(e.target.value))
              }
              options={[7, 14, 30, 60].map((d) => ({
                value: String(d),
                label: t("orgSettings.days", { count: d }),
              }))}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.invoiceFooter")}>
            <Textarea
              size="sm"
              rows={2}
              value={form.invoicing.invoiceFooter}
              onChange={(e) => set("invoicing.invoiceFooter", e.target.value)}
            />
          </FormField>
          <FormField layout="inline" size="sm" label={t("orgSettings.companyLogo")} required>
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-10 w-auto rounded border border-[rgba(11,15,14,0.1)] bg-white p-1"
                />
              ) : (
                <span className="text-[12px] text-[#8A9590]">
                  {t("orgSettings.noLogo")}
                </span>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={handleLogo}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5 mr-1" /> {t("orgSettings.upload")}
              </Button>
            </div>
          </FormField>
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <Button type="button" variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? t("orgSettings.saving") : t("orgSettings.saveSettings")}
        </Button>
      </div>

      <div ref={numberingRef} id="invoice-numbering" className="scroll-mt-4">
        <SectionCard title={t("orgSettings.invoiceNumbering")}>
          <div className="space-y-3">
            <p className="text-[12px] text-[#8A9590]">
              {numbering
                ? t("orgSettings.rangeIntroSet")
                : t("orgSettings.rangeIntro")}
            </p>

            {numbering?.exhausted && (
              <div className="flex items-start gap-2 rounded-lg bg-[#FCE9E9] border border-[#F0C2C1] text-[#7A2A29] px-4 py-2.5 text-[13px]">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                {t("orgSettings.rangeExhausted", {
                  start: numbering.start,
                  end: numbering.end,
                })}
              </div>
            )}
            {lowOnNumbers && (
              <div className="flex items-start gap-2 rounded-lg bg-[#FAEEDA] border border-[#FAC775] text-[#633806] px-4 py-2.5 text-[13px]">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                {t("orgSettings.rangeLow", { count: numbering!.remaining })}
              </div>
            )}

            <FormField
              layout="inline"
              size="sm"
              label={t("orgSettings.rangeStart")}
              required
              labelAdornment={
                numbering?.startLocked ? (
                  <Lock className="h-3 w-3 text-[#8A9590]" aria-hidden />
                ) : undefined
              }
              error={rangeErrors.start}
              hint={
                numbering?.startLocked
                  ? t("orgSettings.rangeStartLocked", {
                      number: numbering.lastIssued,
                    })
                  : t("orgSettings.rangeStartHint")
              }
            >
              <Input
                size="sm"
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                placeholder="1000"
                value={range.start}
                disabled={numbering?.startLocked}
                onChange={(e) =>
                  setRange((r) => ({ ...r, start: e.target.value }))
                }
              />
            </FormField>
            <FormField
              layout="inline"
              size="sm"
              label={t("orgSettings.rangeEnd")}
              required
              error={rangeErrors.end}
              hint={
                numbering?.startLocked
                  ? t("orgSettings.rangeEndHint", { next: numbering.next })
                  : undefined
              }
            >
              <Input
                size="sm"
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                placeholder="100000"
                value={range.end}
                onChange={(e) =>
                  setRange((r) => ({ ...r, end: e.target.value }))
                }
              />
            </FormField>
            {numbering && (
              <FormField layout="inline" size="sm" label={t("orgSettings.rangeStatus")}>
                <p className="py-1 text-[13px] text-[#0B0F0E]">
                  {numbering.exhausted
                    ? t("orgSettings.rangeStatusExhausted")
                    : t("orgSettings.rangeStatusNext", {
                        next: numbering.next,
                        count: numbering.remaining,
                      })}
                  {numbering.lastIssued != null &&
                    ` · ${t("orgSettings.rangeLastIssued", {
                      number: numbering.lastIssued,
                    })}`}
                </p>
              </FormField>
            )}

            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                onClick={handleSaveRange}
                disabled={rangeSaving || !rangeDirty || !rangeValid}
              >
                {rangeSaving
                  ? t("orgSettings.saving")
                  : numbering
                    ? t("orgSettings.saveRange")
                    : t("orgSettings.setRange")}
              </Button>
            </div>
          </div>
        </SectionCard>
      </div>

      <ConfirmDialog
        open={confirmRange}
        tone="warning"
        title={t("orgSettings.rangeConfirmTitle", { start: rangeStart })}
        description={
          <>
            {t("orgSettings.rangeConfirmBody", {
              start: rangeStart,
              end: rangeEnd,
            })}
            {!numbering && (
              <p className="mt-2">{t("orgSettings.rangeConfirmFirst")}</p>
            )}
          </>
        }
        confirmLabel={t("orgSettings.rangeConfirm")}
        onConfirm={saveRange}
        onCancel={() => setConfirmRange(false)}
      />
    </div>
  );
};

export default OrganisationSettings;
